import React, { useState, useEffect } from 'react';
import { Gift, Heart, Check, Sparkles } from 'lucide-react';
import { triggerAchievement } from './AchievementUnlocked';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * DailyLoginReward — Progressive reward calendar with animated chest-opening.
 */
const DailyLoginReward = () => {
  const [data, setData] = useState(null);
  const [claiming, setClaiming] = useState(false);
  const [claimed, setClaimed] = useState(false);
  const [earnedHearts, setEarnedHearts] = useState(0);

  useEffect(() => {
    const token = localStorage.getItem('hibff_auth_token') || sessionStorage.getItem('hibff_auth_token');
    if (!token) return;
    fetch(`${API_URL}/api/engagement/v2/daily-rewards`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null).then(setData).catch(() => {});
  }, []);

  const handleClaim = async () => {
    setClaiming(true);
    try {
      const token = localStorage.getItem('hibff_auth_token') || sessionStorage.getItem('hibff_auth_token');
      const res = await fetch(`${API_URL}/api/engagement/v2/daily-rewards/claim`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      const d = await res.json();
      if (res.ok) {
        setClaimed(true);
        setEarnedHearts(d.hearts_earned);
        setData(prev => ({ ...prev, claimed_today: true, login_streak: d.login_streak }));
        if (d.login_streak === 7 || d.login_streak === 30) {
          triggerAchievement(d.login_streak === 7 ? 'first_streak_week' : 'thirty_day_streak');
        }
      }
    } catch {}
    finally { setClaiming(false); }
  };

  if (!data) return null;

  return (
    <div className="rounded-xl border border-gray-200 bg-white overflow-hidden" data-testid="daily-login-reward">
      <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2 text-white">
          <Gift size={18} />
          <span className="text-sm font-bold">Daily Reward</span>
        </div>
        <span className="text-xs font-bold text-white/80">Day {data.login_streak}</span>
      </div>

      <div className="p-4">
        {/* Reward Calendar (next 7 days) */}
        <div className="flex gap-1.5 mb-3 overflow-x-auto pb-1">
          {data.calendar?.slice(0, 7).map((day, i) => (
            <div
              key={i}
              className={`flex-shrink-0 w-12 text-center p-1.5 rounded-lg border transition-all ${
                day.is_today && !data.claimed_today
                  ? 'border-orange-400 bg-orange-50 shadow-sm'
                  : i === 0 && data.claimed_today
                  ? 'border-green-300 bg-green-50'
                  : 'border-gray-100 bg-gray-50'
              }`}
            >
              <p className="text-[10px] text-gray-400 font-medium">D{day.day}</p>
              <div className="flex items-center justify-center gap-0.5 mt-0.5">
                <Heart size={8} className="text-red-400" fill="currentColor" />
                <span className="text-xs font-bold text-gray-700">{day.hearts}</span>
              </div>
              {i === 0 && data.claimed_today && <Check size={10} className="text-green-500 mx-auto mt-0.5" />}
            </div>
          ))}
        </div>

        {/* Claim button or claimed state */}
        {data.claimed_today || claimed ? (
          <div className="flex items-center justify-center gap-2 py-2 bg-green-50 rounded-lg">
            <Check size={16} className="text-green-600" />
            <span className="text-sm font-semibold text-green-700">
              {claimed ? `+${earnedHearts} Hearts claimed!` : 'Claimed today'}
            </span>
          </div>
        ) : (
          <button
            onClick={handleClaim}
            disabled={claiming}
            className="w-full flex items-center justify-center gap-2 py-2.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-bold text-sm rounded-lg hover:shadow-md transition-all disabled:opacity-50 active:scale-95"
            data-testid="daily-reward-claim-btn"
          >
            {claiming ? (
              <Sparkles size={16} className="animate-spin" />
            ) : (
              <>
                <Gift size={16} />
                Claim +{data.today_reward?.hearts || 5} Hearts
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
};

export default DailyLoginReward;
